import FeatureRequestIssueBadge from "@/src/components/feature-requests/feature-request-issue-badge";
import ScButton from "@/src/components/ui-kit/button";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import { useAuth } from "@/src/contexts/auth.context";
import { FeatureRequest } from "@/src/models/feature-request.model";
import {
  useCreateFeatureRequest,
  useUpdateFeatureRequest,
} from "@/src/services/feature-request/feature-request.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { formatUserName } from "@/src/utils/string.utils";
import { CloseCircle, Edit2, Send2 } from "iconsax-react-nativejs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";

type Mode = "create" | "view" | "edit";

type FeatureRequestModalProps = Readonly<{
  visible: boolean;
  /** The request to show. Omitted = the create form. */
  request?: FeatureRequest | null;
  /** Whether the viewer may edit `request` (its creator or a super admin). */
  canEdit?: boolean;
  onClose: () => void;
}>;

/**
 * Create, view and edit share one modal, GitHub-issue style: viewing shows the
 * title and description as plain text, and the pencil swaps them for the same
 * inputs the create form uses, with Save in place of Create.
 */
export default function FeatureRequestModal({
  visible,
  request,
  canEdit = false,
  onClose,
}: FeatureRequestModalProps) {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const { user } = useAuth();
  const { mutate: createRequest, isPending: isCreating } =
    useCreateFeatureRequest();
  const { mutate: updateRequest, isPending: isUpdating } =
    useUpdateFeatureRequest();

  const [mode, setMode] = useState<Mode>("create");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // What view mode shows — updated after a save so the modal reflects the
  // edit immediately rather than the stale row it was opened with.
  const [saved, setSaved] = useState({ title: "", description: "" });

  useEffect(() => {
    if (!visible) return;
    setMode(request ? "view" : "create");
    setTitle(request?.title ?? "");
    setDescription(request?.description ?? "");
    setSaved({
      title: request?.title ?? "",
      description: request?.description ?? "",
    });
  }, [visible, request]);

  const author = request
    ? {
        userId: request.CreatedBy,
        firstName: request.authorSnapshot?.firstName,
        lastName: request.authorSnapshot?.lastName,
        profileImageUrl: request.authorSnapshot?.profileImageUrl,
      }
    : {
        userId: user?.itemId,
        firstName: user?.firstName,
        lastName: user?.lastName,
        profileImageUrl: user?.profileImageUrl,
      };
  const authorName =
    formatUserName(author.firstName, author.lastName) ||
    t("LABEL_UNKNOWN_USER");

  const isPending = isCreating || isUpdating;
  const canSubmit = !!title.trim() && !isPending;

  const handleSubmit = () => {
    const input = { title: title.trim(), description: description.trim() };
    if (!input.title) return;

    if (mode === "create") {
      createRequest(input, { onSuccess: onClose });
      return;
    }
    if (mode === "edit" && request) {
      updateRequest(
        { itemId: request.ItemId, issueNumber: request.issueNumber, ...input },
        {
          onSuccess: () => {
            setSaved(input);
            setMode("view");
          },
        },
      );
    }
  };

  const mutedColor = themeVars["--color-ash-2"] as string;
  const inputClassName =
    "px-4 py-3 text-sm text-onyx bg-ash border border-ash-3 rounded-xl focus:outline-none";

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        style={[
          varStyle,
          Platform.OS === "web" && ({ backdropFilter: "blur(4px)" } as any),
        ]}
        className="flex-1 items-center justify-center px-6 bg-shadow-dark"
        onPress={onClose}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          className="w-full max-w-3xl max-h-[85%] bg-chalk rounded-2xl border border-ash-3 overflow-hidden"
        >
          {/* Header */}
          <View className="flex-row items-center gap-3 px-4 py-3 border-b border-ash-3">
            <BlocksAvatar
              userId={author.userId}
              fallbackImageUrl={author.profileImageUrl}
              firstName={author.firstName}
              lastName={author.lastName}
              size={36}
            />
            <Text className="flex-1 text-base font-semibold text-onyx">
              {authorName}
            </Text>
            {!!request && <FeatureRequestIssueBadge request={request} />}
            {mode === "view" && canEdit && (
              <Pressable
                onPress={() => setMode("edit")}
                accessibilityLabel={t("LABEL_EDIT_FEATURE_REQUEST")}
                className="p-1.5 rounded-full hover:bg-ash-3"
              >
                <Edit2
                  size={22}
                  color={themeVars["--color-onyx-default"] as string}
                />
              </Pressable>
            )}
            <Pressable
              onPress={onClose}
              className="p-1.5 rounded-full hover:bg-ash-3"
            >
              <CloseCircle
                size={24}
                color={themeVars["--color-onyx-default"] as string}
              />
            </Pressable>
          </View>

          <ScrollView contentContainerClassName="gap-4 p-4">
            {mode === "view" ? (
              <>
                <Text className="text-xl font-bold text-onyx">
                  {saved.title}
                </Text>
                {!!saved.description && (
                  <Text className="text-sm leading-6 text-onyx">
                    {saved.description}
                  </Text>
                )}
              </>
            ) : (
              <>
                <View className="gap-2">
                  <Text className="text-sm font-medium text-onyx">
                    {t("LABEL_TITLE")}
                  </Text>
                  <TextInput
                    value={title}
                    onChangeText={setTitle}
                    placeholder={t("PLACEHOLDER_FEATURE_REQUEST_TITLE")}
                    placeholderTextColor={mutedColor}
                    className={inputClassName}
                    autoFocus
                  />
                </View>
                <View className="gap-2">
                  <Text className="text-sm font-medium text-onyx">
                    {t("LABEL_DESCRIPTION")}
                  </Text>
                  <TextInput
                    value={description}
                    onChangeText={setDescription}
                    placeholder={t("PLACEHOLDER_FEATURE_REQUEST_DESCRIPTION")}
                    placeholderTextColor={mutedColor}
                    multiline
                    textAlignVertical="top"
                    className={`${inputClassName} min-h-[140px]`}
                  />
                </View>
              </>
            )}
          </ScrollView>

          {mode !== "view" && (
            <View className="flex-row justify-end px-4 pb-4">
              <ScButton
                text={mode === "create" ? "BTN_CREATE" : "BTN_SAVE"}
                icon={<Send2 size={20} color="white" />}
                iconPosition="right"
                onPress={handleSubmit}
                disabled={!canSubmit}
                loading={isPending}
              />
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
